// AssistantPanel — reusable Lifted AI chat surface.
// Used by:
//   • <FloatingAssistant /> (corner bubble, every authenticated page)
//   • /launchpad/assistant   (full-page)
//   • Affiliate dashboard sidebar
//   • /admin/ai-workspace
//
// All wiring (session, history, send, reset) lives in useLiftedAssistant.
import React, { useEffect, useRef, useState } from "react";
import { Send, Loader2, RefreshCcw, Sparkles, AlertCircle, Bot } from "lucide-react";
import { useLiftedAssistant } from "@/lib/aiAssistant";

export default function AssistantPanel({
  endpoint = "/ai/claude/chat",
  surface = "floating",
  role,
  placeholder = "Ask me anything about Lifted…",
  title = "X — Lifted AI Assistant",
  subtitle = "Your operating guide for the Lifted ecosystem.",
  className = "",
  initialMessage,
  testid = "assistant-panel",
}) {
  const { messages, busy, error, send, reset, sessionId } = useLiftedAssistant({
    endpoint, surface, role,
  });
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);

  useEffect(() => {
    if (initialMessage && messages.length === 0) {
      send(initialMessage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  function onSubmit(e) {
    e.preventDefault();
    if (!draft.trim() || busy) return;
    send(draft);
    setDraft("");
  }

  return (
    <div
      className={`flex flex-col bg-[rgba(10,10,20,0.96)] backdrop-blur-md border border-white/20 rounded-xl overflow-hidden shadow-2xl ${className}`}
      data-testid={testid}
    >
      <header className="px-4 py-3 border-b border-white/10 bg-gradient-to-r from-lifted-green/15 to-transparent flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="h-4 w-4 text-lifted-green" />
          <div>
            <div className="font-anton uppercase text-white text-base">{title}</div>
            <div className="text-[10px] text-lifted-dim uppercase tracking-[0.18em] font-bebas">{subtitle}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={reset}
          className="text-[10px] uppercase tracking-[0.16em] font-bebas text-lifted-dim hover:text-lifted-green inline-flex items-center gap-1"
          data-testid="assistant-reset"
          title="Start a new conversation"
        >
          <RefreshCcw size={11} /> New
        </button>
      </header>

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[60vh] min-h-[260px]"
        data-testid="assistant-messages"
      >
        {messages.length === 0 && !busy && (
          <div className="text-center text-lifted-dim text-sm py-8 flex flex-col items-center gap-2">
            <Sparkles className="h-6 w-6 text-lifted-green/60" />
            <p className="max-w-md">
              Ask me about products, the affiliate program, Lifted Supplies
              delivery, joining the ecosystem — anything.
            </p>
          </div>
        )}

        {messages.map((m, i) => (
          <div
            key={i}
            className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            data-testid={`msg-${m.role}-${i}`}
          >
            <div
              className={`max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm whitespace-pre-wrap leading-relaxed
                ${m.role === "user"
                  ? "bg-lifted-green/15 text-white border border-lifted-green/30"
                  : "bg-white/5 text-white border border-white/10"}`}
            >
              {m.content}
            </div>
          </div>
        ))}

        {busy && (
          <div className="flex justify-start" data-testid="assistant-busy">
            <div className="px-3.5 py-2.5 rounded-lg bg-white/5 border border-white/10 text-lifted-dim text-sm inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> thinking…
            </div>
          </div>
        )}

        {error && (
          <div
            className="flex items-start gap-2 text-rose-200 text-xs bg-rose-500/10 border border-rose-500/30 rounded-md p-2"
            data-testid="assistant-error"
          >
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-white/10 p-3 flex gap-2 bg-black/30">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          disabled={busy}
          className="flex-1 bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm text-white placeholder:text-lifted-dim focus:outline-none focus:border-lifted-green/50 disabled:opacity-60"
          data-testid="assistant-input"
          maxLength={4000}
          autoComplete="off"
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="px-3 py-2 rounded-md bg-lifted-green/20 hover:bg-lifted-green/30 border border-lifted-green/40 text-lifted-green disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="assistant-send"
          aria-label="Send"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </button>
      </form>

      <div className="px-3 pb-2 text-[10px] text-lifted-dim font-mono flex items-center justify-between">
        <span data-testid="assistant-session-id">
          {sessionId ? `session ${sessionId.slice(0, 8)}` : "no session yet"}
        </span>
        <span>powered by Lifted AI</span>
      </div>
    </div>
  );
}
